import React, { useContext } from 'react';
import { number, string, bool } from 'prop-types';
import { Card, CardActions, Button } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { dataModel } from '../../dataModels/dataModel';
import { QuotesProvider } from '../../context/QuotesContext';
import { defaultCardContentProps, showBelowSmBreakpoint } from '../../constants/responsiveClassNames';
import QuotesHeader from './QuotesHeader';
import QuotesTabsView from './QuotesTabsView';
import QuotesNoTabsView from './QuotesNoTabsView';
import QuotesNullView from './QuotesNullView/QuotesNullView';
import QuotesLoading from './QuotesLoading';
import QuotesError from './QuotesError';
import { useQuoteContext } from '../../hooks/useQuoteContext';
import { triggerAnalyticsCustomEvent } from '../../utils/analyticsEvents';

const QuotesActions = ({ isMobile }) => {
  const viewAllQuotesClick = () => {
    triggerAnalyticsCustomEvent('quotes.stash-click', {
      component: 'view all'
    });
  };
  return isMobile ? (
    <div>
      <CardActions>
        <Button onClick={viewAllQuotesClick} fullWidth href="/quote/list">View All Quotes</Button>
      </CardActions>
    </div>
  ) : null;
};

QuotesActions.propTypes = {
  isMobile: bool
};

QuotesActions.defaultProps = {
  isMobile: false
};

const QuotesContent = ({ maxListSize, componentId }) => {
  const {
    loading,
    error,
    hasQuotes,
    showTabs
  } = useQuoteContext();

  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';

  // Loading skeleton
  if (loading) {
    return <QuotesLoading />;
  }

  // Error state
  if (error) {
    return <QuotesError />;
  }

  if (!hasQuotes) {
    return <QuotesNullView componentId={componentId} />;
  }

  return (
    <Card
      data-component="Quotes"
      data-testid="quotes-container"
      className="sui-h-full"
      CardContentProps={{ className: classNames(defaultCardContentProps, 'sui-h-full') }}
    >
      <div className="sui-@container sui-h-full" id="quotes">
        <div className="sui-flex sui-flex-col sui-gap-4 sui-h-full">
          <QuotesHeader />
          <div
            className="sui-flex sui-flex-col sui-justify-between sui-grow"
          >
            {showTabs ? (
              <QuotesTabsView
                maxListSize={maxListSize}
                componentId={componentId}
                isMobile={isMobile}
              />
            ) : (
              <QuotesNoTabsView maxListSize={maxListSize} componentId={componentId} isMobile={isMobile} />
            )}
            <QuotesActions isMobile={isMobile} />
          </div>
        </div>
      </div>
    </Card>
  );
};

QuotesContent.propTypes = {
  maxListSize: number,
  componentId: string,
};

QuotesContent.defaultProps = {
  maxListSize: 3,
  componentId: '',
};

const Quotes = ({ maxListSize, componentId }) => {
  const {
    containerRef,
  } = useQuoteContext();
  return (
    <QuotesProvider>
      <QuotesContent maxListSize={maxListSize} ref={containerRef} componentId={componentId} />
    </QuotesProvider>
  );
};

Quotes.propTypes = {
  maxListSize: number,
  componentId: string,
};

Quotes.defaultProps = {
  maxListSize: 3,
  componentId: '',
};

Quotes.displayName = 'Quotes';
Quotes.dataModel = dataModel;

export { Quotes };
